import { useEffect, useState } from "react";
import { passwordProblem, stretchPassword } from "@timecapsule/crypto/password";
import { api, googleLoginUrl, returnPath, type MailSent } from "../api";
import { Logo } from "../Logo";
import { Footer, PROBLEM_ADDRESS, PublicHeader, link } from "../App";
import { errorText, useLang, useT, type Messages } from "../i18n";
import { isAborted, passkeysAvailable, signInAssertion } from "../passkey";
import { queryParam } from "../router";
import { LoginFeatures } from "./LoginFeatures";

function describeError(t: Messages, code: string): string {
  const known = t.login.errors[code as "invite_required" | "invite_invalid" | "member_deactivated" | "state_mismatch"];
  if (typeof known === "string") return known;
  if (code.startsWith("google_exchange_failed")) return t.login.errors.google;
  return t.login.errors.other(code);
}

export function Login() {
  const t = useT();
  const { lang } = useLang();
  const invite = queryParam("invite");
  const error = queryParam("error");
  const recover = queryParam('recover')==='1';

  return (
    <>
      <PublicHeader />
      <main className="landing">
        <section className="hero">
          <div className="hero-text">
            <h1>{t.login.heroTitle}</h1>
            <p className="lead">{t.login.lead}</p>
            <LoginFeatures />
          </div>
          <div className="hero-side">
            <div className="hero-card card">
              <h2>{t.login.signInTitle}</h2>
              {invite && <p className="note small">{t.login.invite}</p>}
              {error && <p className="error">{describeError(t, error)}</p>}
              <div className="stack">
                <a className="button primary" href={googleLoginUrl(invite)}>
                  {recover?t.pilotAdmin.recoverGoogle:t.login.google}
                </a>
                {passkeysAvailable() && <PasskeySignIn />}
              </div>
              {recover&&<p className="small">{t.pilotAdmin.recoverHelp}</p>}
              <PasswordForms invite={invite} recover={recover}/>
              <p className="small"><a href={recover?'/':'/?recover=1'}>{recover?t.verify.home:t.pilotAdmin.recover}</a></p>
              {!invite && <p className="small"><a href="#pilot">{t.pilot.noInvite}</a></p>}
              <p className="muted small legal-signin-note">
                {t.legal.loginNote}{" "}<a href="/terms" onClick={link("/terms")}>{t.footer.terms}</a>.
                {" "}{t.legal.loginPrivacy}{" "}<a href="/privacy" onClick={link("/privacy")}>{t.footer.privacy}</a>.
              </p>
            </div>
            <p className="muted small hero-side-note">
              {t.login.privacy}{" "}
              <a href="/guide" onClick={link("/guide")}>
                {t.login.guide}
              </a>
            </p>
            {lang !== "en" && lang !== "sr" && <p className="muted small hero-side-note">{t.footer.translationNote}</p>}
          </div>
        </section>
        {/* PLAN.md 4.33: what it does first, then how it works and why it can be trusted, the story last. */}
        <section className="landing-steps" aria-labelledby="landing-steps-title">
          <h2 id="landing-steps-title">{t.login.steps.title}</h2>
          <ol>{t.login.steps.items.map((item) => <li key={item}>{item}</li>)}</ol>
          <p className="trust-point small">
            {t.login.heroPoints[2]}{" "}
            <a href="/guide" onClick={link("/guide")}>{t.login.trustLink}</a>
          </p>
        </section>
        <section className="landing-story" aria-labelledby="landing-story-title">
          <Logo height={56} title="" />
          <div className="story-text">
            <p className="story-eyebrow">{t.login.story.eyebrow}</p>
            <h2 id="landing-story-title">{t.login.story.title}</h2>
            <p>{t.login.story.myth} {t.login.story.reflection}</p>
            <p className="story-closing">{t.login.story.closing}</p>
          </div>
        </section>
        <PilotCall />
        {import.meta.env.DEV && <DevLogin invite={invite} />}
      </main>
      <Footer />
    </>
  );
}

/** The open call for pilot testers: what to send, and a mail link with that template filled in. */
function PilotCall() {
  const t = useT();
  const mail = `mailto:${PROBLEM_ADDRESS}?subject=${encodeURIComponent(t.pilot.subject)}&body=${encodeURIComponent(t.pilot.body)}`;
  // The header badge links here from other pages; the section exists only after this render.
  useEffect(() => {
    if (location.hash === "#pilot") document.getElementById("pilot")?.scrollIntoView();
  }, []);
  return (
    <section id="pilot" className="pilot-call card" aria-labelledby="pilot-title">
      <div className="pilot-text">
        <h2 id="pilot-title">{t.pilot.title}</h2>
        <p>{t.pilot.intro}</p>
        <p className="note small">{t.pilot.backupNote}</p>
        <p>{t.pilot.how}</p>
        <ul>{t.pilot.fields.map((field) => <li key={field}>{field}</li>)}</ul>
      </div>
      <div className="pilot-side">
        <a className="button primary" href={mail}>{t.pilot.button}</a>
        <a href={`mailto:${PROBLEM_ADDRESS}`}>{PROBLEM_ADDRESS}</a>
        <p className="muted small">{t.pilot.use}</p>
      </div>
    </section>
  );
}

/** Passkeys the member enrolled while protecting their key; the browser lists them, no address needed. */
function PasskeySignIn() {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    setError(null);
    setBusy(true);
    try {
      const { challenge, token } = await api.passkeyOptions();
      await api.passkeyLogin(token, await signInAssertion(challenge));
      location.href = returnPath();
    } catch (err) {
      if (!isAborted(err)) setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <button className="button" disabled={busy} onClick={signIn}>
        {busy ? t.login.passkey.busy : t.login.passkey.button}
      </button>
      {error && <p className="error small">{error}</p>}
    </>
  );
}

type Mode = "signin" | "register" | "reset";

/**
 * Email + password (PLAN.md 4.14). The password is stretched in the browser before it is sent;
 * registration and reset finish through a mail link (/verify).
 */
function PasswordForms({ invite, recover }: { invite: string | null; recover: boolean }) {
  const t = useT();
  const { lang } = useLang();
  const [mode, setMode] = useState<Mode>(invite||recover ? "register" : "signin");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<MailSent | null>(null);

  const problem = mode === "reset" || password.length === 0 ? null : passwordProblem(password);
  const mismatch = mode === "register" && repeat.length > 0 && password !== repeat;
  const ready = email.includes("@") && (mode === "reset" || (password.length > 0 && !problem && (mode === "signin" || password === repeat)));

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setSent(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "reset") {
        setSent(await api.passwordReset({ email, lang }));
      } else {
        const stretched = await stretchPassword(password, email);
        if (mode === "signin") {
          await api.passwordLogin({ email, stretched });
          location.href = returnPath();
          return;
        }
        setSent(await api.passwordRegister({ email, stretched, name: name.trim() || undefined, invite: invite ?? undefined, lang, recover }));
      }
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <section className="password-forms">
      <div className="chips">
        {(!recover?["signin", "register"] as const:[]).map((m) => (
          <button key={m} type="button" className={mode === m ? "chip on" : "chip"} onClick={() => switchTo(m)}>
            {t.login.password.modes[m]}
          </button>
        ))}
      </div>
      {sent ? (
        <>
          <p>{mode === "reset" ? t.login.password.resetSent : t.login.password.checkMail(email)}</p>
          {sent.dev_link && (
            <p className="small">
              Dev: <a href={sent.dev_link}>{sent.dev_link}</a>
            </p>
          )}
        </>
      ) : (
        <form onSubmit={submit}>
          {mode === "reset" && <p className="small">{t.login.password.resetText}</p>}
          <label>
            {t.login.password.email}
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {mode === "register" && (
            <label>
              {t.login.password.name}
              <input autoComplete="name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          )}
          {mode !== "reset" && (
            <label>
              {t.login.password.password}
              <input
                type="password"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}
          {mode === "register" && (
            <label>
              {t.login.password.repeat}
              <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
            </label>
          )}
          {problem && mode === "register" && <p className="error small">{t.login.password.rule}</p>}
          {mismatch && <p className="error small">{t.login.password.differ}</p>}
          <div className="password-actions">
            <button className="primary" type="submit" disabled={!ready || busy}>
              {busy ? t.login.password.busy : t.login.password.submit[mode]}
            </button>
            {mode === "signin" && (
              <button type="button" className="link small" onClick={() => switchTo("reset")}>
                {t.login.password.forgot}
              </button>
            )}
          </div>
          {mode === "register" && <p className="muted small">{t.login.password.registerNote}</p>}
        </form>
      )}
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

/** Local development only (stripped from production builds): logs in through POST /api/auth/dev. Not translated. */
function DevLogin({ invite }: { invite: string | null }) {
  const [email, setEmail] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.devLogin({ email, name: email.split("@")[0], secret, invite: invite ?? undefined });
      location.href = returnPath();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form className="card dev" onSubmit={submit}>
      <h3>Dev login (localhost only)</h3>
      <input placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input placeholder="DEV_LOGIN_SECRET" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} />
      <button type="submit">Log in</button>
      {error && <p className="error">{error}</p>}
    </form>
  );
}
